/**
 * tm/pages/FundAdvisorPage.tsx — 정책자금 추천(설문)
 *  2026-10-30 지도사 「설문조사처럼 클릭클릭 → 최종 추천」 · 2026-10-31 「요건 · 이력은 한 장에 하나씩 말고 다다닥 클릭」 「PC · 모바일 모두 최적화」
 *  2026-10-31 지도사 「톤앤매너를 일치시켜 — 대출 점검 화면이 우리 톤앤매너야」
 *   → 대출 점검(LoanBoardPage)과 같은 규격: 회색 본문 위 흰 카드(테두리 · 둥근 12) · 머리 카드(제목 T.lg 800 + 회색 한 줄 + 진행 막대 8px)
 *     · 갈래 단추 = 남색 알약(고른 것 채움 + 숫자 배지) · 구역 = 윗선 + 제목 T.md 800 · 단추 높이 32 · 글자 T.sm(진한 단추는 남색 하나)
 *     · 상태 배지 = 대출 점검 TONE(초록 · 노랑 · 회색) · 단계 원 = 청록 · 글자 크기는 T(11~18) 안에서만
 *   · PC 전용(2026-10-31 「모바일용은 필요 없어 — 다 PC에서 해」): ≥ 1024 왼쪽 질문 카드 + 오른쪽 「지금까지 추천」 카드 · 좁은 창은 한 칸 · 단추 줄은 카드 아래
 *   · 키보드(PC) — 1~9 고르기 · Enter 다음 · Backspace/← 뒤로
 *  2026-10-31 「크레탑 · 재무제표 먼저 · 확인되는 건 빼고 나머지만」 → 시작 카드에서 PDF 올리기(services/fundDoc)
 *   · 읽은 답은 질문에서 건너뜀 · 최종 「고른 답」에 「크레탑」 배지로 남아 고칠 수 있음 · 인증처럼 일부만 아는 건 미리 체크해 그대로 물음
 *  2026-10-31 「운전 · 시설 · 직접 · 대리 · 이차보전 구분」 → 줄마다 용도 · 방식 표시 + 최종 화면 용도 · 방식 거르기(1~3순위도 그 안에서 다시)
 *  다음 질문 · 말투(ask · sub · presets) · 묶음 · 판정은 서버(services/fundAdvisor) 한 곳
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, DragEvent, ReactNode } from 'react';
import { req, apiBase, apiToken } from '../api';
import { C, T } from '../components/ui';
import { useWindowWidth } from '../../shared';

interface QOpt { value: string; label: string }
interface Question { key: string; sec: string; label: string; ask: string; sub: string; presets: number[]; kind: 'one' | 'many' | 'num' | 'select'; unit?: string; options: QOpt[]; board: string; group: string; boardTop: [string, string] | null }
interface Section { key: string; label: string }
interface Reason { ok: boolean | null; t: string; src?: string; todo?: string }
interface Terms { limit: string; rate: string; period: string; method: string }
type Verdict = 'ok' | 'prep' | 'check' | 'no';
interface Item { key: string; org: 'kosmes' | 'semas'; orgLabel: string; grp: string; name: string; terms: Terms; src: string; uses: string[]; ways: string[]; verdict: Verdict; reasons: Reason[]; path: string[]; todo?: boolean; fit?: string[] }
interface View { use: '' | 'work' | 'facility'; way: '' | 'direct' | 'agency' | 'subsidy' | 'other' }
interface Legend { key: string; label: string; t: string }
interface Final { headline: string; picks: Array<{ key: string; rank: number; why: string }>; more: string[]; prep: Array<{ key: string; path: string[] }>; ask: Array<{ t: string; fund: string }> }
interface Result { micro: boolean | null; route: string; planB: string; hints: string[]; rate: { kosmes: string; semas: string }; limit: string; visible: string[]; items: Item[]; final: Final; summary: string; version: string; legend: Legend[]; match: string[] }
type Val = string | string[] | number | undefined;
type Answers = Record<string, Val>;
type Screen = 'intro' | 'q' | 'final';
interface DocFact { key: string; label: string; value: string; note?: string }
interface DocRead { kind: 'cretop' | 'fin' | 'none'; company: string; answers: Answers; partial: Answers; facts: DocFact[]; notes: string[]; message?: string }
const USE_TXT: Record<string, string> = { work: '운전', facility: '시설', build: '사업장 확보' };
const WAY_TXT: Record<string, string> = { direct: '직접대출', agency: '대리대출', subsidy: '이차보전', invest: '투융자', bond: '회사채', joint: '공동 금융지원', etc: '그 밖 방식' };
const VIEW_USE: Array<[View['use'], string]> = [['', '전체'], ['work', '운전'], ['facility', '시설']];
const VIEW_WAY: Array<[View['way'], string]> = [['', '전체'], ['direct', '직접대출'], ['agency', '대리대출'], ['subsidy', '이차보전'], ['other', '그 밖']];
/** 서버 services/fundAdvisor.viewMatch 와 같은 셈 — 거르기 단추 숫자용 */
const viewMatch = (x: Item, v: View) => {
  if (v.use === 'work' && !x.uses.includes('work')) return false;
  if (v.use === 'facility' && !x.uses.some(u => u === 'facility' || u === 'build')) return false;
  if (v.way === 'other') return x.ways.some(w => !['direct', 'agency', 'subsidy'].includes(w));
  if (v.way && !x.ways.includes(v.way)) return false;
  return true;
};

/* ── 대출 점검과 같은 색 · 모양 ── */
const NAVY = '#1B3A5C', NAVY_LT = '#EEF3F8', TEAL = '#0A7D74', TEAL_LT = '#E6F4F1', G6 = '#6B7280', LINE = '#F1F2F4', PANEL = '#F8FAFC';
const VERD: Record<Verdict, [string, string, string]> = {   // [바탕, 글자, 이름] — 대출 점검 TONE
  ok: ['#D1FAE5', '#047857', '신청 가능'], prep: [NAVY_LT, NAVY, '준비하면 가능'], check: ['#FEF3C7', '#92400E', '확인 필요'], no: ['#F3F4F6', '#4B5563', '지금은 어려움'],
};
const ORG: Record<Item['org'], [string, string]> = { kosmes: [NAVY_LT, NAVY], semas: [TEAL_LT, TEAL] };
const KA: CSSProperties = { wordBreak: 'keep-all', overflowWrap: 'anywhere' };
const cardSt: CSSProperties = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, minWidth: 0 };
const CSS = `
.fa button{font-family:inherit}
.fa button:focus-visible,.fa input:focus-visible{outline:2px solid ${NAVY};outline-offset:2px}
.fa .fa-row:hover{background:#F9FAFB}
.fa .fa-row[aria-checked="true"]:hover,.fa .fa-row[aria-current="true"]:hover{background:${NAVY_LT}}
.fa .fa-kbd{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 4px;border-radius:4px;border:1px solid ${C.border};background:#F9FAFB;font-size:${T.xs}px;font-weight:700;color:${G6};box-sizing:border-box}
.fa input::placeholder{color:#C4C9D0}
`;

/** 단추 — 대출 점검 btn() 그대로(높이 32 · T.sm · 진한 단추는 남색 하나) */
function Btn({ children, onClick, kind = 's', disabled, style, ...aria }: { children: ReactNode; onClick: () => void; kind?: 'p' | 's' | 'link'; disabled?: boolean; style?: CSSProperties; 'aria-label'?: string }) {
  const base: CSSProperties = kind === 'p' ? { height: 32, padding: '0 16px', borderRadius: 8, background: disabled ? '#CBD5E1' : NAVY, color: '#fff', border: 'none' }
    : kind === 's' ? { height: 32, padding: '0 14px', borderRadius: 8, background: '#fff', color: disabled ? C.muted : C.text, border: `1.5px solid ${C.borderDk}` }
      : { height: 32, padding: 0, borderRadius: 6, background: 'none', color: C.sub, border: 'none', textDecoration: 'underline', textUnderlineOffset: 3 };
  return <button type="button" disabled={disabled} onClick={onClick} aria-label={aria['aria-label']} style={{ fontSize: T.sm, fontWeight: kind === 'link' ? 600 : 700, whiteSpace: 'nowrap', cursor: disabled ? 'default' : 'pointer', ...base, ...style }}>{children}</button>;
}
/** 남색 알약 — 대출 점검 「오늘 볼 것 7 · 전체 7」 */
function Pill({ on, children, n, onClick, label }: { on: boolean; children: ReactNode; n?: number; onClick: () => void; label?: string }) {
  return (
    <button type="button" role="radio" aria-checked={on} aria-label={label} onClick={onClick}
      style={{ height: 30, padding: n !== undefined ? '0 6px 0 14px' : '0 14px', borderRadius: 18, border: `1px solid ${on ? NAVY : C.border}`, background: on ? NAVY : '#fff', color: on ? '#fff' : C.sub, fontSize: T.sm, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
      {children}
      {n !== undefined && <b style={{ minWidth: 20, height: 20, padding: '0 4px', boxSizing: 'border-box', borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: T.xs, background: on ? 'rgba(255,255,255,.2)' : '#F3F4F6', color: on ? '#fff' : C.sub }}>{n}</b>}
    </button>
  );
}
/** 고르기 칩 — 대출 점검 갈래 고르기(테두리 1.5 · 고르면 남색 채움 + ✓) */
function Chip({ on, children, onClick, k }: { on: boolean; children: ReactNode; onClick: () => void; k?: number }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 30, padding: '5px 14px', boxSizing: 'border-box', borderRadius: 16, border: `1.5px solid ${on ? NAVY : '#CBD5E1'}`, background: on ? NAVY : '#fff', color: on ? '#fff' : C.text, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', textAlign: 'left', maxWidth: '100%', lineHeight: '18px', ...KA }}>
      {k !== undefined && <span style={{ fontSize: T.xs, fontWeight: 700, color: on ? 'rgba(255,255,255,.7)' : C.muted }}>{k}</span>}
      <span>{on ? '✓ ' : ''}{children}</span>
    </button>
  );
}
function Badge({ children, bg, color, line }: { children: ReactNode; bg: string; color: string; line?: boolean }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 12, boxSizing: 'border-box', background: bg, color, border: line ? '1px solid #CBD5E1' : 'none', fontSize: T.xs, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>{children}</span>;
}
/** 용도(운전 · 시설 · 사업장 확보 — 회색) · 방식(직접 · 대리 · 이차보전 · 투융자 … — 테두리) — 결과 줄 · 오른쪽 「먼저 권할 자금」 같은 모양 */
function TagRow({ x, mt = 6 }: { x: Item; mt?: number }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: mt }} aria-label={`용도 ${x.uses.map(u => USE_TXT[u]).join(' · ')} · 방식 ${x.ways.map(w => WAY_TXT[w]).join(' · ')}`}>
      {x.uses.map(u => <Badge key={u} bg="#F3F4F6" color="#4B5563">{USE_TXT[u] || u}</Badge>)}
      <span aria-hidden style={{ width: 1, height: 12, background: C.border, margin: '0 3px' }} />
      {x.ways.map(w => <Badge key={w} bg="#fff" color="#4B5563" line>{WAY_TXT[w] || w}</Badge>)}
    </span>
  );
}
/** 고르기 표시 — 하나 고르기 = 원 · 여럿 = 네모 */
function Mark({ on, radio }: { on: boolean; radio?: boolean }) {
  return (
    <span aria-hidden style={{ flexShrink: 0, width: 18, height: 18, borderRadius: radio ? 9 : 5, boxSizing: 'border-box', border: `1.5px solid ${on ? NAVY : '#CBD5E1'}`, background: on && !radio ? NAVY : '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {on && (radio ? <i style={{ width: 8, height: 8, borderRadius: 4, background: NAVY }} /> : <svg width="10" height="8" viewBox="0 0 12 10"><path d="M1.5 5.2 4.6 8.2 10.5 1.8" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>)}
    </span>
  );
}
const Chevron = ({ open }: { open?: boolean }) => <svg width="16" height="16" viewBox="0 0 20 20" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><path d="m5 8 5 5 5-5" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;

const unitTxt = (n: number, unit?: string) => (unit === '억' && n < 1 ? `${Math.round(n * 10000).toLocaleString()}만원` : `${n.toLocaleString()}${unit || ''}`);
function answerText(q: Question, v: Val): string {
  if (v === undefined) return '';
  if (q.kind === 'num') return unitTxt(Number(v), q.unit);
  if (Array.isArray(v)) return v.length ? v.map(x => q.options.find(o => o.value === x)?.label || x).join(' · ') : '해당 없어요';
  return q.options.find(o => o.value === v)?.label || String(v);
}

export default function FundAdvisorPage({ onToast }: { onToast: (m: string, k?: 'success' | 'error') => void }) {
  const ww = useWindowWidth();
  const wide = ww >= 1024;   // PC 전용(2026-10-31 지도사 「모바일용은 필요 없어 — 다 PC에서 해」) · 1024 아래(좁은 노트북 창)만 한 칸으로
  const [qs, setQs] = useState<Question[] | null>(null);
  const [secs, setSecs] = useState<Section[]>([]);
  const [visible, setVisible] = useState<string[]>([]);
  const [a, setA] = useState<Answers>({});
  const [cur, setCur] = useState('');
  const [screen, setScreen] = useState<Screen>('intro');
  const [numStr, setNumStr] = useState('');
  const [company, setCompany] = useState('');
  const [res, setRes] = useState<Result | null>(null);
  const [flash, setFlash] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState('');
  const [view, setView] = useState<View>({ use: '', way: '' });
  const [doc, setDoc] = useState<DocRead | null>(null);
  const [docBusy, setDocBusy] = useState(false);
  const [docMsg, setDocMsg] = useState('');
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const seq = useRef(0);
  const advanceFrom = useRef<string | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);

  const order = useMemo(() => (qs || []).map(q => q.key), [qs]);
  const qOf = useMemo(() => new Map((qs || []).map(q => [q.key, q])), [qs]);
  const visAll = useMemo(() => order.filter(k => visible.includes(k)), [order, visible]);
  // 크레탑 · 재무제표에서 읽은 답(그 값 그대로일 때)은 묻지 않는다 — 지우거나 고치면 다시 질문에 들어옴
  const docKeys = useMemo(() => new Set(doc ? Object.keys(doc.answers) : []), [doc]);
  const fromDoc = useCallback((k: string) => !!doc && docKeys.has(k) && JSON.stringify(a[k]) === JSON.stringify(doc.answers[k]), [doc, docKeys, a]);
  const hidden = useRef<(k: string) => boolean>(() => false);
  hidden.current = fromDoc;
  const visQs = useMemo(() => visAll.filter(k => !fromDoc(k)), [visAll, fromDoc]);
  /** 화면 단위 — 같은 묶음(board)이 이웃하면 한 화면 · [[key…], …] */
  const screens = useMemo(() => {
    const out: string[][] = [];
    visQs.forEach(k => { const b = qOf.get(k)?.board; const last = out[out.length - 1]; if (b && last && qOf.get(last[0])?.board === b) last.push(k); else out.push([k]); });
    return out;
  }, [visQs, qOf]);
  const runOf = useCallback((k: string) => screens.find(r => r.includes(k)) || [k], [screens]);

  const toTop = () => scroller.current?.scrollTo({ top: 0 });
  const go = useCallback((from: string, vis: string[]) => {
    const vs = new Set(vis);
    const n = order.slice(order.indexOf(from) + 1).find(k => vs.has(k) && !hidden.current(k)) || '';
    setFlash('');
    if (n) { setCur(n); setScreen('q'); } else setScreen('final');
    toTop();
  }, [order]);

  useEffect(() => {
    req<{ sections: Section[]; questions: Question[]; visible: string[] }>('GET', '/fund-advisor')
      .then(r => { setQs(r.data.questions); setSecs(r.data.sections); setVisible(r.data.visible); setCur(r.data.visible[0] || ''); })
      .catch(() => setErr('질문을 불러오지 못했어요. 잠시 뒤 다시 열어 주세요.'));
  }, []);
  // 답이 바뀌면 판정 · 노출 — 늦게 온 옛 응답은 버림 · 기다리던 「다음」은 새 노출로(실패하면 2.5초 뒤 지금 노출로)
  useEffect(() => {
    if (!qs) return undefined;
    const n = ++seq.current;
    const fail = setTimeout(() => { if (n === seq.current && advanceFrom.current) { const f = advanceFrom.current; advanceFrom.current = null; go(f, visible); } }, 2500);
    req<Result>('POST', '/fund-advisor/judge', { answers: a, company, view })
      .then(r => {
        if (n !== seq.current) return;
        setRes(r.data); setVisible(r.data.visible);
        if (advanceFrom.current) { const f = advanceFrom.current; advanceFrom.current = null; go(f, r.data.visible); }
      })
      .catch(() => { /* fail 타이머가 넘김 */ })
      .finally(() => clearTimeout(fail));
    return () => clearTimeout(fail);
  }, [a, company, qs, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = cur ? qOf.get(cur) : undefined;
  useEffect(() => { const v = q ? a[q.key] : undefined; setNumStr(typeof v === 'number' ? String(v) : ''); }, [cur]); // eslint-disable-line react-hooks/exhaustive-deps

  const answer = (k: string, v: Val) => {
    if (JSON.stringify(a[k]) === JSON.stringify(v)) { go(k, visible); return; }
    advanceFrom.current = k;
    setA(x => { const y = { ...x }; if (v === undefined) delete y[k]; else y[k] = v; return y; });
  };
  const pickOne = (k: string, v: string) => { setFlash(v); setTimeout(() => answer(k, v), 160); };
  const toggle = (k: string, v: string) => setA(x => {
    const c0 = Array.isArray(x[k]) ? (x[k] as string[]) : [];
    return { ...x, [k]: c0.includes(v) ? c0.filter(y => y !== v) : [...c0, v] };
  });
  const skip = () => { if (!q) return; if (a[q.key] !== undefined) answer(q.key, undefined); else go(q.key, visible); };
  const back = () => {
    if (screen === 'final') { setScreen('q'); setCur((screens[screens.length - 1] || [''])[0]); toTop(); return; }
    if (!q) return;
    const i = screens.findIndex(r => r.includes(q.key));
    if (i > 0) setCur(screens[i - 1][0]); else setScreen('intro');
    toTop();
  };
  /** 묶음 화면 「다음」 — 본 여럿 고르기 중 안 고른 건 「해당 없음」([]) · 하나 고르기는 고른 것만 */
  const commitBoard = (run: string[]) => {
    const lastKey = run[run.length - 1];
    const patch: Answers = {};
    run.forEach(k => { const qq = qOf.get(k); if (qq?.kind === 'many' && !Array.isArray(a[k])) patch[k] = []; });
    if (!Object.keys(patch).length) { go(lastKey, visible); return; }
    advanceFrom.current = lastKey;
    setA(x => ({ ...x, ...patch }));
  };
  const skipBoard = (run: string[]) => go(run[run.length - 1], visible);
  const setOne = (k: string, v: string) => setA(x => { const y = { ...x }; if (y[k] === v) delete y[k]; else y[k] = v; return y; });
  const editAt = (k: string) => { setCur(k); setScreen('q'); toTop(); };
  /** 크레탑 · 재무제표 PDF 읽기 — 서버(fundDoc)가 읽은 답 · 미리 체크할 것 · 읽은 정보 줄을 돌려준다 */
  async function readFile(file: File) {
    if (docBusy) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') { setDocMsg('PDF 파일만 읽을 수 있어요. 사진이면 답을 직접 골라 주세요.'); return; }
    setDocBusy(true); setDocMsg('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${apiBase()}/fund-advisor/read`, { method: 'POST', headers: { Authorization: 'Bearer ' + apiToken() }, body: fd });
      const j = (await r.json().catch(() => null)) as { success?: boolean; data?: DocRead; error?: string } | null;
      if (!j || !j.success || !j.data) { setDocMsg((j && j.error) || '읽지 못했어요. 잠시 뒤 다시 올려 주세요.'); return; }
      const d = j.data;
      if (d.kind === 'none') { setDocMsg(d.message || '크레탑 보고서나 재무제표로 읽지 못했어요.'); return; }
      // 다른 파일로 바꾸면 앞 파일에서 온 답은 지우고 새로 채움(손으로 고른 답은 그대로)
      setA(x => {
        const y: Answers = { ...x };
        if (doc) [...Object.keys(doc.answers), ...Object.keys(doc.partial)].forEach(k => { const o = doc.answers[k] !== undefined ? doc.answers[k] : doc.partial[k]; if (JSON.stringify(y[k]) === JSON.stringify(o)) delete y[k]; });
        return { ...y, ...d.partial, ...d.answers };
      });
      setDoc(d);
      if (d.company && (!company || (doc && company === doc.company))) setCompany(d.company.slice(0, 60));
    } catch { setDocMsg('읽지 못했어요. 잠시 뒤 다시 올려 주세요.'); }
    finally { setDocBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  }
  function reset() { setA({}); setOpen({}); setFlash(''); setDoc(null); setDocMsg(''); setView({ use: '', way: '' }); advanceFrom.current = null; setCur(order.find(k => visible.includes(k)) || order[0] || ''); setScreen('intro'); toTop(); }
  async function copy() {
    if (!res) return;
    try { await navigator.clipboard.writeText(res.summary); onToast('결과를 복사했어요. 카톡이나 문자에 붙여 넣으면 돼요', 'success'); }
    catch { onToast('복사하지 못했어요. 다시 눌러 주세요', 'error'); }
  }

  /* ── 키보드(PC) — 1~9 고르기 · Enter 다음 · Backspace 뒤로 ── */
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => undefined);
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  /* ── 틀 — 대출 점검과 같음: 스크롤 칸 · 머리 카드 · (PC) 왼쪽 본 카드 + 오른쪽 카드 · 단추 줄(휴대폰은 화면 아래에 붙음) ── */
  const okN = res ? res.items.filter(x => x.verdict === 'ok').length : 0;
  const prepN = res ? res.items.filter(x => x.verdict === 'prep').length : 0;
  const docName = doc && doc.kind === 'cretop' ? '크레탑' : '재무제표';
  const page = (o: { main: ReactNode; side?: ReactNode; cta?: ReactNode; progress?: number; hint?: ReactNode }) => {
    const step: Screen = screen;
    const head = (
      <div style={{ ...cardSt, padding: '16px 20px', display: 'grid', gridTemplateColumns: wide ? 'auto 1fr auto' : '1fr', gap: wide ? 20 : 12, alignItems: 'center' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: T.lg, fontWeight: 800, whiteSpace: 'nowrap' }}>정책자금 추천</div>
          <div style={{ fontSize: T.sm, color: C.sub, marginTop: 2, ...KA }}>{[company, doc ? `${docName}에서 ${doc.facts.length}가지 읽음` : '', '2026 중진공 · 소진공 공고 기준'].filter(Boolean).join(' · ')}</div>
        </div>
        {wide && <span />}
        {o.progress !== undefined ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifySelf: wide ? 'end' : 'stretch', minWidth: 0 }}>
            <span style={{ fontSize: T.md, whiteSpace: 'nowrap' }}>질문 <b>{Math.round(o.progress * 100)}%</b> 지났어요</span>
            <span role="progressbar" aria-valuenow={Math.round(o.progress * 100)} aria-valuemin={0} aria-valuemax={100} style={{ flex: wide ? 'none' : 1, width: wide ? 220 : undefined, height: 8, borderRadius: 4, background: C.border, overflow: 'hidden' }}>
              <i style={{ display: 'block', height: '100%', width: `${o.progress * 100}%`, background: '#10B981', transition: 'width .3s ease' }} />
            </span>
          </div>
        ) : (wide ? <span /> : null)}
        <div role="radiogroup" aria-label="단계" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', gridColumn: '1 / -1', alignItems: 'center' }}>
          <Pill on={step === 'intro'} onClick={() => { setScreen('intro'); toTop(); }}>시작</Pill>
          <Pill on={step === 'q'} onClick={() => { if (step !== 'q') { setCur((screens[0] || [order[0]])[0]); setScreen('q'); toTop(); } }}>질문</Pill>
          <Pill on={step === 'final'} n={okN} label={`추천 보기 · 신청 가능 ${okN}개`} onClick={() => { setScreen('final'); toTop(); }}>추천 보기</Pill>
        </div>
      </div>
    );
    const ctaBar = o.cta && (
      <div style={{ borderTop: `1px solid ${C.border}`, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ flex: 1, fontSize: T.sm, color: G6 }}>{o.hint}</span>
        {o.cta}
      </div>
    );
    return (
      <div ref={scroller} className="fa" style={{ height: '100%', overflowY: 'auto', overscrollBehavior: 'contain', display: 'flex', flexDirection: 'column' }}>
        <style>{CSS}</style>
        {/* 칸 너비 · 여백 = 대출 점검 그대로(2026-11-01 지도사 「칸 너비 · 폰트 크기 · 스타일 톤앤매너 맞게」): 최대 1440 · 왼쪽 440 목록 칸 | 오른쪽 본문 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 20, maxWidth: 1440, margin: '0 auto', boxSizing: 'border-box', width: '100%', flex: 1 }}>
          {head}
          <div style={{ display: 'grid', gridTemplateColumns: wide && o.side ? 'minmax(0,440px) minmax(0,1fr)' : 'minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
            {o.side && wide && <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0, position: 'sticky', top: 0 }}>{o.side}</div>}
            <div style={{ ...cardSt, overflow: 'hidden' }}>{o.main}{ctaBar}</div>
            {o.side && !wide && <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>{o.side}</div>}
          </div>
        </div>
      </div>
    );
  };
  /** 카드 머리(제목 T.lg 800 · 위 회색 한 줄 · 아래 설명) */
  const top = (title: ReactNode, sub?: ReactNode, upper?: ReactNode, right?: ReactNode) => (
    <div style={{ padding: '18px 20px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {upper && <div style={{ fontSize: T.sm, color: G6, fontWeight: 600, marginBottom: 4, ...KA }}>{upper}</div>}
        <h2 style={{ margin: 0, fontSize: T.lg, fontWeight: 800, color: C.text, lineHeight: 1.45, ...KA }}>{title}</h2>
        {sub && <div style={{ fontSize: T.sm, color: C.sub, marginTop: 4, lineHeight: 1.6, ...KA }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
  /** 구역 — 대출 점검 sec(윗선 · 제목 T.md 800) */
  const sec = (title: ReactNode, body: ReactNode, right?: ReactNode, key?: string) => (
    <div key={key} style={{ padding: '16px 20px', borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, minHeight: 24 }}><span style={{ fontSize: T.md, color: C.text, fontWeight: 800, flex: 1, ...KA }}>{title}</span>{right}</div>
      {body}
    </div>
  );
  const two = (l: ReactNode, r: ReactNode) => <div style={{ display: 'grid', gridTemplateColumns: '128px 128px', gap: 8 }}>{l}{r}</div>;
  const kbdHint = <>키보드 <span className="fa-kbd">1</span>~<span className="fa-kbd">9</span> 고르기 · <span className="fa-kbd">Enter</span> 다음 · <span className="fa-kbd">←</span> 뒤로</>;

  if (err) { keyRef.current = () => undefined; return page({ main: top('잠시 문제가 생겼어요', err) }); }
  if (!qs) { keyRef.current = () => undefined; return page({ main: <div style={{ padding: 28, textAlign: 'center', color: G6, fontSize: T.sm }}>질문을 불러오고 있어요</div> }); }

  const itemOf = (k: string) => res?.items.find(x => x.key === k);
  const start = () => { setCur((screens[0] || [order[0]])[0]); setScreen('q'); toTop(); };

  /* 오른쪽 카드(PC) — 지금까지 추천(통화하며 바로 말할 수 있게) */
  const livePanel = res && (
    <div style={cardSt}>
      <div style={{ padding: '16px 20px 4px', fontSize: T.md, fontWeight: 800 }}>지금까지 답으로 보면</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '8px 20px 12px' }}>
        {([['신청 가능', okN, '#047857'], ['준비하면 가능', prepN, NAVY]] as Array<[string, number, string]>).map(([l, n, col]) => (
          <div key={l} style={{ padding: '10px 12px', borderRadius: 10, background: PANEL, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: T.sm, color: G6 }}>{l}</div>
            <div style={{ fontSize: T.xl, fontWeight: 800, color: col, fontVariantNumeric: 'tabular-nums' }}>{n}<span style={{ fontSize: T.sm, color: G6, fontWeight: 600, marginLeft: 2 }}>개</span></div>
          </div>
        ))}
      </div>
      {res.final.picks.length > 0 && sec('먼저 권할 자금', (
        <div style={{ display: 'grid', gap: 10 }}>
          {res.final.picks.map(p => { const x = itemOf(p.key); return x ? (
            <div key={p.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 11, background: p.rank === 1 ? NAVY : NAVY_LT, color: p.rank === 1 ? '#fff' : NAVY, fontSize: T.xs, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{p.rank}</span>
              <div style={{ minWidth: 0 }}><div style={{ fontSize: T.sm, fontWeight: 700, ...KA }}>{x.orgLabel} {x.name}</div><TagRow x={x} mt={4} /><div style={{ fontSize: T.sm, color: G6, marginTop: 4, ...KA }}>{x.terms.limit}</div></div>
            </div>) : null; })}
        </div>
      ))}
      <div style={{ padding: '12px 20px 16px', borderTop: `1px solid ${C.border}`, display: 'grid', gap: 10 }}>
        <div style={{ fontSize: T.sm, color: C.sub, ...KA }}>{res.route}</div>
        <Btn onClick={() => { setScreen('final'); toTop(); }} style={{ width: '100%' }}>지금까지로 추천 보기</Btn>
      </div>
    </div>
  );

  /* ── 시작 ── */
  if (screen === 'intro') {
    keyRef.current = (e) => { if (e.key === 'Enter' && !(e.target instanceof HTMLInputElement)) { e.preventDefault(); start(); } };
    const pick = () => fileRef.current?.click();
    const onDrop = (e: DragEvent) => { e.preventDefault(); setDrag(false); const fl = e.dataTransfer.files && e.dataTransfer.files[0]; if (fl) void readFile(fl); };
    const fileIn = <input ref={fileRef} type="file" accept="application/pdf,.pdf" hidden onChange={e => { const fl = e.target.files && e.target.files[0]; if (fl) void readFile(fl); }} />;
    const upload = (
      <div onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop} data-doc>
        {fileIn}
        {!doc ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 10, border: `1.5px dashed ${drag ? NAVY : C.borderDk}`, background: drag ? NAVY_LT : PANEL }}>
            <svg width="22" height="22" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><path d="M7 3h7l5 5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm7 0v5h5M9.5 13h6m-6 3.5h6" fill="none" stroke={NAVY} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: T.md, fontWeight: 700 }}>{docBusy ? '파일을 읽고 있어요' : '크레탑 보고서나 재무제표 PDF'}</div>
              <div style={{ fontSize: T.sm, color: C.sub, marginTop: 2, ...KA }}>{docBusy ? '몇 초면 돼요' : '올리면 거기 나온 건 묻지 않아요 · 끌어다 놓아도 돼요'}</div>
            </div>
            <Btn onClick={pick} disabled={docBusy}>{docBusy ? '읽는 중' : '파일 고르기'}</Btn>
          </div>
        ) : (
          <div style={{ borderRadius: 10, border: `1px solid ${drag ? NAVY : C.border}`, background: drag ? NAVY_LT : PANEL, padding: '10px 14px' }}>
            {doc.facts.map(fc => (
              <div key={fc.key} style={{ display: 'grid', gridTemplateColumns: '84px minmax(0,1fr)', alignItems: 'baseline', minHeight: 28, padding: '4px 0', fontSize: T.sm }}>
                <span style={{ color: G6 }}>{fc.label}</span>
                <span style={{ minWidth: 0, ...KA }}><b style={{ fontVariantNumeric: 'tabular-nums' }}>{fc.value}</b>{fc.note && <span style={{ color: G6, marginLeft: 6 }}>{fc.note}</span>}</span>
              </div>
            ))}
            {doc.notes.map((nt, i) => <div key={`n${i}`} style={{ marginTop: 6, padding: '8px 10px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: T.sm, color: '#92400E', ...KA }}>{nt}</div>)}
          </div>
        )}
        {docMsg && <div role="alert" style={{ marginTop: 8, fontSize: T.sm, fontWeight: 700, color: C.red }}>{docMsg}</div>}
      </div>
    );
    const steps = (
      <div style={{ display: 'grid', gap: 10 }}>
        {[['공고 기준', '2026 중진공 · 소진공 자금 48종의 요건 · 제한 · 예외를 맞춰 봐요'], ['답에 맞는 질문만', '업종 · 규모 · 이력에 따라 필요한 것만 물어요'], ['요건 · 이력은 한 화면에', '희망리턴 · 판로개척 · 스마트공장 같은 건 칩으로 한 번에 눌러요'], ['끝나면 추천 순서', '1 · 2 · 3순위와 먼저 할 일 · 운전/시설 · 직접/대리/이차보전을 알려 드려요']].map(([t, d], i) => (
          <div key={t} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 11, background: TEAL_LT, border: `1px solid ${TEAL}`, color: TEAL, fontSize: T.xs, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>{i + 1}</span>
            <div style={{ minWidth: 0 }}><div style={{ fontSize: T.sm, fontWeight: 700 }}>{t}</div><div style={{ fontSize: T.sm, color: C.sub, ...KA }}>{d}</div></div>
          </div>
        ))}
      </div>
    );
    const main = (
      <>
        {top('정책자금, 맞는 걸 찾아 드릴게요', '고객과 통화하면서 하나씩 눌러 주세요. 잘 모르는 건 건너뛰어도 돼요.')}
        {sec(doc ? `${docName}에서 ${doc.facts.length}가지를 읽었어요 — 이건 묻지 않아요` : '크레탑 · 재무제표 먼저(선택)', upload, doc ? <Btn kind="link" onClick={pick} disabled={docBusy}>{docBusy ? '읽는 중' : '다른 파일'}</Btn> : undefined)}
        {sec('업체 이름(선택)', (
          <input id="fa-co" value={company} onChange={e => setCompany(e.target.value.slice(0, 60))} placeholder="예: (주)로움" aria-label="업체 이름"
            style={{ width: '100%', maxWidth: 420, height: 40, border: `1.5px solid ${C.borderDk}`, background: '#fff', borderRadius: 8, padding: '0 12px', fontSize: T.md, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        ))}
        {!wide && sec('이렇게 진행돼요', steps)}
      </>
    );
    const side = wide ? <div style={cardSt}>{sec('이렇게 진행돼요', steps)}</div> : undefined;
    const startBtn = <Btn kind="p" onClick={start} disabled={docBusy} style={{ minWidth: 160 }}>{doc ? '나머지 질문 시작하기' : '시작하기'}</Btn>;
    return page({ main, side, cta: startBtn, hint: <><span className="fa-kbd">Enter</span> 로 시작해요</> });
  }

  /* ── 질문 ── */
  if (screen === 'q' && q) {
    const run = runOf(q.key);
    const isBoard = !!q.board && run.length > 0 && !!qOf.get(run[0])?.board;
    const sIdx = Math.max(0, screens.findIndex(r => r.includes(q.key)));
    const pos = sIdx + 1;
    const isLast = sIdx >= screens.length - 1;
    const nextLabel = isLast ? '추천 보기' : '다음';
    const secName = secs.find(s => s.key === q.sec)?.label || '';
    const upper = fromDoc(q.key) ? `${secName} · ${docName}에서 읽은 답` : `${secName} · ${pos} / ${screens.length}`;
    const progress = (pos - 1) / Math.max(1, screens.length);
    const side = wide ? livePanel || undefined : undefined;
    const backBtn = <Btn onClick={back} aria-label="뒤로" style={{ flexShrink: 0, padding: '0 10px' }}>‹ 뒤로</Btn>;

    /* 묶음 화면 — 요건 · 이력을 칩으로 다다닥 */
    if (isBoard) {
      const qq0 = qOf.get(run[0]) as Question;
      const [bt, bs] = qq0.boardTop || ['해당하는 걸 모두 눌러 주세요', ''];
      const picked = run.reduce((n, k) => { const v = a[k]; return n + (Array.isArray(v) ? v.length : v !== undefined ? 1 : 0); }, 0);
      const flat: Array<() => void> = [];
      const main = (
        <div data-q={run[0]} data-board={qq0.board}>
          {top(bt, bs, upper, backBtn)}
          {run.map(k => {
            const qq = qOf.get(k) as Question;
            const v = a[k];
            return (
              <div key={k} data-group={k}>
                {sec(<>{qq.group || qq.ask}{qq.kind !== 'many' && <span style={{ fontSize: T.sm, fontWeight: 500, color: G6, marginLeft: 6 }}>하나만</span>}</>, (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {qq.options.map(o => {
                      const on = qq.kind === 'many' ? Array.isArray(v) && v.includes(o.value) : v === o.value;
                      const fn = () => (qq.kind === 'many' ? toggle(k, o.value) : setOne(k, o.value));
                      flat.push(fn);
                      return <Chip key={o.value} on={on} onClick={fn} k={flat.length <= 9 ? flat.length : undefined}>{o.label}</Chip>;
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      );
      keyRef.current = (e) => {
        if (e.target instanceof HTMLInputElement) return;
        if (/^[1-9]$/.test(e.key) && flat[Number(e.key) - 1]) { e.preventDefault(); flat[Number(e.key) - 1](); }
        else if (e.key === 'Enter') { e.preventDefault(); commitBoard(run); }
        else if (e.key === 'Backspace' || e.key === 'ArrowLeft') { e.preventDefault(); back(); }
      };
      const cta = two(<Btn onClick={() => skipBoard(run)}>잘 모르겠어요</Btn>, <Btn kind="p" onClick={() => commitBoard(run)}>{picked ? `${picked}개 고르고 ${nextLabel}` : `해당 없이 ${nextLabel}`}</Btn>);
      return page({ main, side, cta, progress, hint: kbdHint });
    }

    const v = a[q.key];
    const many = q.kind === 'many';
    const vals = many && Array.isArray(v) ? v : [];
    const numOk = numStr.trim() !== '' && numStr !== '.';
    const commitNum = () => answer(q.key, numOk ? Number(numStr) : undefined);
    const optFns = q.options.map(o => () => (many ? toggle(q.key, o.value) : pickOne(q.key, o.value)));
    const rowSt = (on: boolean): CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 46, padding: '10px 20px', boxSizing: 'border-box', border: 'none', borderBottom: `1px solid ${LINE}`, background: on ? NAVY_LT : '#fff', textAlign: 'left', cursor: 'pointer' });
    const main = (
      <div data-q={q.key}>
        {top(q.ask, q.sub || (many ? '해당하는 걸 모두 골라 주세요' : undefined), upper, backBtn)}
        {q.kind === 'num' ? (
          <div style={{ padding: '16px 20px 20px', borderTop: `1px solid ${C.border}` }}>
            <form onSubmit={e => { e.preventDefault(); commitNum(); }} style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 360 }}>
              <input autoFocus value={numStr} onChange={e => setNumStr(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))} inputMode="decimal" enterKeyHint="next" aria-label={q.ask} placeholder="0"
                style={{ flex: 1, minWidth: 0, height: 44, border: `1.5px solid ${numOk ? NAVY : '#9CA3AF'}`, background: '#fff', borderRadius: 8, padding: '0 14px', fontSize: T.xl, fontWeight: 800, fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box', color: C.text }} />
              {q.unit && <span style={{ fontSize: T.lg, fontWeight: 700, color: C.sub, whiteSpace: 'nowrap' }}>{q.unit}</span>}
            </form>
            {q.presets.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                {q.presets.map(p => <Chip key={p} on={numStr === String(p)} onClick={() => setNumStr(String(p))}>{unitTxt(p, q.unit)}</Chip>)}
              </div>
            )}
          </div>
        ) : (
          <div role={many ? 'group' : 'radiogroup'} aria-label={q.ask} style={{ borderTop: `1px solid ${C.border}` }}>
            {q.options.map((o, i) => {
              const on = many ? vals.includes(o.value) : (flash ? flash === o.value : v === o.value);
              return (
                <button key={o.value} type="button" role={many ? 'checkbox' : 'radio'} aria-checked={on} className="fa-row" onClick={optFns[i]} style={rowSt(on)}>
                  <Mark on={on} radio={!many} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: T.md, fontWeight: on ? 700 : 500, color: on ? NAVY : C.text, lineHeight: 1.5, ...KA }}>{o.label}</span>
                  {i < 9 && <span className="fa-kbd" aria-hidden>{i + 1}</span>}
                </button>
              );
            })}
            {many && (() => {
              const none = flash === '__none' || (Array.isArray(v) && !vals.length);
              return (
                <button type="button" role="checkbox" aria-checked={none} className="fa-row" onClick={() => { setFlash('__none'); setA(x => ({ ...x, [q.key]: [] })); setTimeout(() => answer(q.key, []), 160); }} style={{ ...rowSt(none), borderBottom: 'none' }}>
                  <Mark on={none} />
                  <span style={{ flex: 1, fontSize: T.md, fontWeight: none ? 700 : 500, color: none ? NAVY : C.sub }}>해당하는 게 없어요</span>
                </button>
              );
            })()}
          </div>
        )}
      </div>
    );
    keyRef.current = (e) => {
      const inInput = e.target instanceof HTMLInputElement;
      if (!inInput && /^[1-9]$/.test(e.key) && optFns[Number(e.key) - 1]) { e.preventDefault(); optFns[Number(e.key) - 1](); }
      else if (e.key === 'Enter' && !inInput && many && vals.length) { e.preventDefault(); answer(q.key, vals); }
      else if (!inInput && (e.key === 'Backspace' || e.key === 'ArrowLeft')) { e.preventDefault(); back(); }
    };
    const cta = q.kind === 'one' || q.kind === 'select'
      ? <div style={{ display: 'grid', gridTemplateColumns: '128px' }}><Btn onClick={skip}>잘 모르겠어요</Btn></div>
      : two(<Btn onClick={skip}>잘 모르겠어요</Btn>, <Btn kind="p" disabled={many ? !vals.length : !numOk} onClick={() => (q.kind === 'num' ? commitNum() : answer(q.key, vals))}>{many && vals.length ? `${vals.length}개 고르고 ${nextLabel}` : nextLabel}</Btn>);
    return page({ main, side, cta, progress, hint: kbdHint });
  }

  /* ── 최종 추천 ── */
  if (!res) return page({ main: <div style={{ padding: 28, textAlign: 'center', color: G6, fontSize: T.sm }}>추천을 만들고 있어요</div> });
  const f = res.final;
  const lead = (bg: string, fg: string, body: ReactNode, label?: string) => (
    <span aria-label={label} style={{ flexShrink: 0, width: 26, height: 26, borderRadius: 13, background: bg, color: fg, fontSize: T.sm, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>{body}</span>
  );
  const ICON: Record<Verdict, string> = { ok: 'M6 12.5 10.2 16.5 18 8', prep: 'M5 12h12m-5-5 5 5-5 5', check: 'M9.5 9.2a2.6 2.6 0 1 1 3.6 2.4c-.7.3-1.1.9-1.1 1.6v.5M12 17v.4', no: 'M8 8l8 8m0-8-8 8' };
  const vLead = (vd: Verdict) => lead(VERD[vd][0], VERD[vd][1], <svg width="14" height="14" viewBox="0 0 24 24"><path d={ICON[vd]} fill="none" stroke={VERD[vd][1]} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>, VERD[vd][2]);
  const rankLead = (n: number) => lead(n === 1 ? NAVY : NAVY_LT, n === 1 ? '#fff' : NAVY, n, `${n}순위`);
  const fundRow = (x: Item, ld: ReactNode, why?: string) => {
    const isOpen = !!open[x.key];
    const [obg, ofg] = ORG[x.org];
    return (
      <div key={x.key} style={{ borderBottom: `1px solid ${LINE}` }}>
        <button type="button" className="fa-row" onClick={() => setOpen(o => ({ ...o, [x.key]: !isOpen }))} aria-expanded={isOpen}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', padding: '12px 4px', boxSizing: 'border-box', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}>
          {ld}
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Badge bg={obg} color={ofg}>{x.orgLabel}</Badge>
              <span style={{ fontSize: T.md, fontWeight: 800, color: x.verdict === 'no' ? G6 : C.text, ...KA }}>{x.name}</span>
            </span>
            <TagRow x={x} />
            <span style={{ display: 'block', marginTop: 6, fontSize: T.sm, color: C.sub, fontVariantNumeric: 'tabular-nums', ...KA }}>{x.terms.limit} · {x.terms.rate}</span>
            {why && <span style={{ display: 'block', marginTop: 4, fontSize: T.sm, fontWeight: 600, color: '#047857', ...KA }}>{why}</span>}
            {x.verdict === 'check' && <span style={{ display: 'block', marginTop: 4, fontSize: T.sm, fontWeight: 600, color: '#92400E', ...KA }}>확인할 것 · {x.reasons.filter(r => r.ok === null).map(r => r.t).join(' · ')}</span>}
            {x.verdict === 'no' && <span style={{ display: 'block', marginTop: 4, fontSize: T.sm, color: G6, ...KA }}>{x.reasons.filter(r => r.ok === false).map(r => r.t).join(' · ')}</span>}
          </span>
          <Chevron open={isOpen} />
        </button>
        {x.path.length > 0 && (
          <ol style={{ listStyle: 'none', margin: 0, padding: '0 4px 12px 42px', display: 'grid', gap: 6 }}>
            {x.path.map((p, j) => (
              <li key={j} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 10, boxSizing: 'border-box', background: j === 0 ? TEAL_LT : '#fff', border: `1px solid ${j === 0 ? TEAL : '#D1D5DB'}`, color: j === 0 ? TEAL : G6, fontSize: T.xs, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{j + 1}</span>
                <span style={{ fontSize: T.sm, color: C.text, lineHeight: '20px', ...KA }}>{p}</span>
              </li>
            ))}
          </ol>
        )}
        {isOpen && (
          <div style={{ margin: '0 4px 12px 42px', padding: '10px 12px', borderRadius: 10, background: PANEL, border: `1px solid ${C.border}`, display: 'grid', gap: 6 }}>
            {x.reasons.map((r, j) => (
              <div key={j} style={{ display: 'flex', gap: 8, fontSize: T.sm }}>
                <span style={{ flexShrink: 0, width: 14, fontWeight: 800, color: r.ok === true ? '#047857' : r.ok === null ? '#92400E' : C.red }}>{r.ok === true ? '✓' : r.ok === null ? '?' : '✕'}</span>
                <span style={{ color: C.text, ...KA }}>{r.t}{r.src && <span style={{ color: G6 }}> · {r.src}</span>}</span>
              </div>
            ))}
            <div style={{ fontSize: T.sm, color: G6, marginTop: 2, ...KA }}>기간 {x.terms.period} · {x.terms.method} · 근거 {x.src}</div>
          </div>
        )}
      </div>
    );
  };
  const foldRow = (id: string, t: string, list: Item[]) => list.length > 0 && (
    <div key={id} style={{ borderBottom: `1px solid ${LINE}` }}>
      <button type="button" className="fa-row" onClick={() => setOpen(o => ({ ...o, [id]: !o[id] }))} aria-expanded={!!open[id]}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 44, padding: '0 4px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: T.md, fontWeight: 700 }}>{t}</span>
        <b style={{ minWidth: 20, height: 20, padding: '0 6px', boxSizing: 'border-box', borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: T.xs, background: '#F3F4F6', color: C.sub }}>{list.length}</b>
        <span style={{ flex: 1 }} /><span style={{ fontSize: T.sm, color: G6 }}>{open[id] ? '접기' : '펼치기'}</span><Chevron open={!!open[id]} />
      </button>
      {open[id] && list.map(x => fundRow(x, vLead(x.verdict)))}
    </div>
  );
  const pickSet = new Set(f.picks.map(p => p.key)), prepSet = new Set(f.prep.map(p => p.key));
  const inView = new Set(res.match || res.items.map(x => x.key));
  const viewItems = res.items.filter(x => inView.has(x.key));
  const answered = visAll.filter(k => a[k] !== undefined);
  const live = res.items.filter(x => x.verdict === 'ok' || x.verdict === 'prep');
  const cnt = (v: View) => live.filter(x => viewMatch(x, v)).length;
  const wayNote = view.way === 'other' ? '성장공유형 · 투자조건부(투융자) · 회사채 · 공동 금융지원 · 매출채권 팩토링 같은 방식이에요' : (res.legend || []).find(l => l.key === view.way)?.t || '';
  const filterRow = (label: string, body: ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
      <span style={{ flexShrink: 0, width: 28, fontSize: T.sm, color: G6, lineHeight: '30px' }}>{label}</span>
      <div role="radiogroup" aria-label={label} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>{body}</div>
    </div>
  );
  const filterBox = (
    <div style={{ padding: '0 20px 16px', display: 'grid', gap: 8 }} data-filter>
      {filterRow('용도', VIEW_USE.map(([v, l]) => <Pill key={v || 'all'} on={view.use === v} n={cnt({ use: v, way: view.way })} label={`용도 ${l}`} onClick={() => setView(o => ({ ...o, use: v }))}>{l}</Pill>))}
      {filterRow('방식', VIEW_WAY.map(([v, l]) => <Pill key={v || 'all'} on={view.way === v} n={cnt({ use: view.use, way: v })} label={`방식 ${l}`} onClick={() => setView(o => ({ ...o, way: v }))}>{l}</Pill>))}
      {wayNote && <div style={{ fontSize: T.sm, color: C.sub, paddingLeft: 38, ...KA }}>{wayNote}</div>}
    </div>
  );
  const notes = [res.planB, ...res.hints, res.limit, res.rate.kosmes, res.rate.semas].filter(Boolean);
  keyRef.current = (e) => { if (!(e.target instanceof HTMLInputElement) && (e.key === 'Backspace' || e.key === 'ArrowLeft')) { e.preventDefault(); back(); } };
  const mainPart = (
    <div data-final>
      {top(f.headline, res.route, `${company ? `${company} · ` : ''}최종 추천`, <Btn onClick={back} aria-label="뒤로" style={{ flexShrink: 0, padding: '0 10px' }}>‹ 뒤로</Btn>)}
      {filterBox}
      {f.picks.length > 0 && sec('이 순서로 신청해 보세요', <div style={{ marginTop: -6 }}>{f.picks.map(p => { const x = itemOf(p.key); return x ? fundRow(x, rankLead(p.rank), p.why) : null; })}</div>)}
      {f.prep.length > 0 && sec('이걸 먼저 하면 신청할 수 있어요', <div style={{ marginTop: -6 }}>{f.prep.map(p => { const x = itemOf(p.key); return x ? fundRow(x, vLead('prep')) : null; })}</div>)}
      {f.ask.length > 0 && sec(<>이것만 더 물어보세요<span style={{ fontSize: T.sm, fontWeight: 500, color: G6, marginLeft: 8 }}>답하면 추천이 더 정확해져요</span></>, (
        <div style={{ display: 'grid', gap: 8 }}>
          {f.ask.map((x, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '18px minmax(0,1fr)', gap: 6 }}>
              <span style={{ fontSize: T.sm, fontWeight: 800, color: '#92400E' }}>?</span>
              <div style={{ minWidth: 0 }}><div style={{ fontSize: T.sm, fontWeight: 700, ...KA }}>{x.t}</div><div style={{ fontSize: T.sm, color: G6, ...KA }}>{x.fund}</div></div>
            </div>
          ))}
        </div>
      ))}
      {notes.length > 0 && sec('참고', (
        <div style={{ padding: '10px 12px', borderRadius: 10, background: PANEL, border: `1px solid ${C.border}`, display: 'grid', gap: 6 }}>
          {notes.map((h, i) => <div key={i} style={{ fontSize: T.sm, color: C.sub, ...KA }}>{h}</div>)}
        </div>
      ))}
      <div style={{ padding: '12px 20px 16px', borderTop: `1px solid ${C.border}`, fontSize: T.sm, color: C.muted, ...KA }}>{res.version} · 한도와 금리는 공고 기준이고, 실제 금액은 기업평가로 정해져요</div>
    </div>
  );
  const foldItems = [
    foldRow('ok', '그 밖에 신청할 수 있는 자금', viewItems.filter(x => x.verdict === 'ok' && !pickSet.has(x.key))),
    foldRow('prep', '준비하면 신청할 수 있는 자금', viewItems.filter(x => x.verdict === 'prep' && !prepSet.has(x.key))),
    foldRow('check', '확인이 더 필요한 자금', viewItems.filter(x => x.verdict === 'check')),
    foldRow('no', '지금은 신청하기 어려운 자금', viewItems.filter(x => x.verdict === 'no')),
  ].filter(Boolean);
  const folds = foldItems.length > 0 && <div style={cardSt}>{sec('나머지 자금', <div style={{ marginTop: -6 }}>{foldItems}</div>)}</div>;
  const answersCard = (
    <div style={cardSt}>
      {sec(<>고른 답 {answered.length}개<span style={{ fontSize: T.sm, fontWeight: 500, color: G6, marginLeft: 8 }}>누르면 고칠 수 있어요</span></>, (
        <div style={{ marginTop: -6 }}>
          {answered.map(k => { const qq = qOf.get(k); return qq ? (
            <button key={k} type="button" className="fa-row" onClick={() => editAt(k)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 4px', border: 'none', borderBottom: `1px solid ${LINE}`, background: 'transparent', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: T.sm, color: G6, ...KA }}>{qq.board ? qq.group : qq.ask}{fromDoc(k) && <Badge bg="#F3F4F6" color={C.sub}>{docName}</Badge>}</span>
                <span style={{ display: 'block', fontSize: T.sm, fontWeight: 700, color: C.text, marginTop: 1, ...KA }}>{answerText(qq, a[k])}</span>
              </span>
              <svg width="14" height="14" viewBox="0 0 16 16" style={{ flexShrink: 0 }}><path d="m6 3 5 5-5 5" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>) : null; })}
        </div>
      ))}
    </div>
  );
  const finalCta = two(<Btn onClick={reset}>처음부터</Btn>, <Btn kind="p" onClick={() => void copy()}>결과 복사하기</Btn>);
  return page({ main: mainPart, side: <>{folds}{answersCard}</>, cta: finalCta });
}
